import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BaseResizableComponent } from '@src/app/modules/base';
import { AlertService } from '@src/core/services';
import { catchError, takeUntil, throwError } from 'rxjs';
import { SettingsService } from '@src/api-messenger';

import { FormDataControls, FormData } from './types';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent extends BaseResizableComponent {
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly messengerSettingsService = inject(SettingsService);

  form = new FormGroup<FormDataControls>({
    sendPushNotifications: new FormControl(false, { nonNullable: true }),
    showMessageInPush: new FormControl(false, { nonNullable: true }),
  });

  loading = false;

  override ngOnInit(): void {
    super.ngOnInit();

    this.form.valueChanges.pipe(takeUntil(this.destroyed$$)).subscribe(() => {
      this.save();
    });

    this.getData();
  }

  save() {
    if (!this.form.dirty || this.form.disabled || this.loading) {
      return;
    }

    this.loading = true;
    this.form.disable();

    const formData = this.form.value as FormData;

    this.messengerSettingsService
      .apiAppSettingsUserNotificationPost(formData)
      .pipe(
        takeUntil(this.destroyed$$),
        catchError(err => {
          this.alertService.error(err);
          this.form.enable();
          this.loading = false;

          return throwError(() => err);
        }),
      )
      .subscribe(() => {
        this.alertService.success(
          this.translateService.instant('components.messenger.alerts.successes.saveNotificationsConfig'),
        );

        this.getData();

        this.form.enable();
        this.cdr.markForCheck();
      });
  }

  private getData() {
    this.loading = true;

    this.messengerSettingsService
      .apiAppSettingsUserNotificationGet()
      .pipe(
        takeUntil(this.destroyed$$),
        catchError(err => {
          this.alertService.error(err);
          this.loading = false;
          this.cdr.markForCheck();

          return throwError(() => err);
        }),
      )
      .subscribe(configs => {
        this.form.reset(configs);

        this.loading = false;
        this.cdr.markForCheck();
      });
  }
}
